// src/theme.js

import { createTheme, alpha } from "@mui/material/styles";

// --- TEMA MINIMALISTA ESTILO INSTAGRAM/THREADS ---

const fonts = {
  heading: '"Outfit", "Roboto", "Helvetica", "Arial", sans-serif',
  body: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
};

// Paleta Minimalista - Colores Suaves
const primaryColor = "#F97316";        // Naranja principal (mantener identidad)
const primaryLight = "#FB923C";         // Naranja claro
const primaryDark = "#EA580C";          // Naranja oscuro

// Colores de fondo suaves (no blanco puro ni negro profundo)
const lightBg = "#FAFAFA";              // Gris muy claro (Instagram style)
const lightPaper = "#FFFFFF";           // Blanco para cards
const lightBorder = "#E5E5E5";          // Borde sutil

const darkBg = "#121212";               // Gris oscuro suave (no negro puro)
const darkPaper = "#1E1E1E";            // Paper oscuro
const darkBorder = "#2A2A2A";           // Borde oscuro sutil

const shape = {
  borderRadius: 8, // Bordes más pequeños, estilo minimalista
};

// Función helper para crear tipografía responsive
const createResponsiveTypography = (headingFont, bodyFont) => {
  const baseTheme = createTheme();
  const { breakpoints } = baseTheme;

  return {
    fontFamily: bodyFont,
    h1: {
      fontFamily: headingFont,
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      [breakpoints.up('sm')]: {
        fontSize: '3rem',
      },
      [breakpoints.up('md')]: {
        fontSize: '4rem',
      },
      [breakpoints.up('lg')]: {
        fontSize: '5rem',
      },
      [breakpoints.up('xl')]: {
        fontSize: '6rem',
      },
    },
    h2: {
      fontFamily: headingFont,
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
      [breakpoints.up('sm')]: {
        fontSize: '2.5rem',
      },
      [breakpoints.up('md')]: {
        fontSize: '3rem',
      },
      [breakpoints.up('lg')]: {
        fontSize: '3.5rem',
      },
      [breakpoints.up('xl')]: {
        fontSize: '3.75rem',
      },
    },
    h3: {
      fontFamily: headingFont,
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.4,
      [breakpoints.up('sm')]: {
        fontSize: '2rem',
      },
      [breakpoints.up('md')]: {
        fontSize: '2.5rem',
      },
      [breakpoints.up('lg')]: {
        fontSize: '2.75rem',
      },
      [breakpoints.up('xl')]: {
        fontSize: '3rem',
      },
    },
    h4: {
      fontFamily: headingFont,
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      [breakpoints.up('sm')]: {
        fontSize: '1.75rem',
      },
      [breakpoints.up('md')]: {
        fontSize: '2rem',
      },
      [breakpoints.up('lg')]: {
        fontSize: '2.125rem',
      },
    },
    h5: {
      fontFamily: headingFont,
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
      [breakpoints.up('sm')]: {
        fontSize: '1.375rem',
      },
      [breakpoints.up('md')]: {
        fontSize: '1.5rem',
      },
    },
    h6: {
      fontFamily: headingFont,
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
      [breakpoints.up('sm')]: {
        fontSize: '1.125rem',
      },
      [breakpoints.up('md')]: {
        fontSize: '1.25rem',
      },
    },
    body1: {
      fontFamily: bodyFont,
      fontSize: '0.875rem',
      lineHeight: 1.6,
      [breakpoints.up('sm')]: {
        fontSize: '0.9375rem',
      },
      [breakpoints.up('md')]: {
        fontSize: '1rem',
      },
    },
    body2: {
      fontFamily: bodyFont,
      fontSize: '0.8125rem',
      lineHeight: 1.5,
      [breakpoints.up('sm')]: {
        fontSize: '0.875rem',
      },
    },
    button: {
      fontFamily: headingFont,
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.875rem',
      [breakpoints.up('sm')]: {
        fontSize: '0.9375rem',
      },
      [breakpoints.up('md')]: {
        fontSize: '1rem',
      },
    },
    caption: {
      fontFamily: bodyFont,
      fontSize: '0.75rem',
      [breakpoints.up('sm')]: {
        fontSize: '0.8125rem',
      },
      [breakpoints.up('md')]: {
        fontSize: '0.875rem',
      },
    },
    overline: {
      fontFamily: bodyFont,
      fontSize: '0.6875rem',
      [breakpoints.up('sm')]: {
        fontSize: '0.75rem',
      },
      [breakpoints.up('md')]: {
        fontSize: '0.8125rem',
      },
    },
  };
};

// --- Tema Claro Minimalista ---
const lightTheme = createTheme({
  typography: createResponsiveTypography(fonts.heading, fonts.body),
  shape,
  palette: {
    mode: "light",
    primary: {
      main: primaryColor,
      light: primaryLight,
      dark: primaryDark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: primaryDark,
      light: primaryLight,
      dark: primaryDark,
      contrastText: '#FFFFFF',
    },
    background: {
      default: lightBg,              // Gris muy claro, no blanco puro
      paper: lightPaper,              // Blanco para cards
      subtle: "#F5F5F5",             // Gris sutil
    },
    text: {
      primary: "#1A1A1A",            // Casi negro pero más suave
      secondary: "#737373",          // Gris medio suave
      disabled: "#A3A3A3",          // Gris claro
    },
    divider: lightBorder,             // Borde sutil
    error: {
      main: "#EF4444",
      contrastText: '#FFFFFF',
    },
    warning: {
      main: "#F59E0B",
      contrastText: '#FFFFFF',
    },
    info: {
      main: "#3B82F6",
      contrastText: '#FFFFFF',
    },
    success: {
      main: "#10B981",
      contrastText: '#FFFFFF',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          padding: '8px 20px',
          borderRadius: 8,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
          }
        },
        containedPrimary: {
          backgroundColor: primaryColor,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: primaryDark,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
          }
        },
        outlinedPrimary: {
          borderColor: lightBorder,
          color: primaryColor,
          '&:hover': {
            borderColor: primaryColor,
            backgroundColor: alpha(primaryColor, 0.04),
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: lightPaper,
          border: `1px solid ${lightBorder}`,
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: lightPaper,
          borderBottom: `1px solid ${lightBorder}`,
          color: '#1A1A1A',
          boxShadow: 'none',
        }
      }
    },
    MuiDialog: {
      defaultProps: {
        maxWidth: 'md',
        fullWidth: true,
      },
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          '@media (min-width: 900px)': {
            maxWidth: '85vw !important',
          },
          '@media (max-width: 600px)': {
            margin: 0,
            width: '100%',
            height: '100%',
            maxHeight: '100%',
            borderRadius: 0,
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          '@media (max-width: 600px)': {
            padding: '12px 16px',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          '@media (max-width: 600px)': {
            padding: '16px',
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          '@media (max-width: 600px)': {
            padding: '12px 16px',
            flexDirection: 'column',
            '& > *': {
              width: '100% !important',
              marginLeft: '0 !important',
              marginRight: '0 !important',
              marginBottom: '8px',
            },
            '& > *:last-child': {
              marginBottom: 0,
            },
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&:before': {
            display: 'none',
          },
          '@media (max-width: 600px)': {
            margin: '0 !important',
            borderRadius: '0 !important',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            padding: '4px 12px',
            minHeight: '48px !important',
            '&.Mui-expanded': {
              minHeight: '48px !important',
            },
          },
        },
        content: {
          '@media (max-width: 600px)': {
            margin: '8px 0 !important',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            padding: '8px 12px 12px',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: alpha(primaryColor, 0.1),
          color: primaryDark,
          fontWeight: 600,
        }
      }
    }
  }
});

// --- Tema Oscuro Minimalista ---
const darkTheme = createTheme({
  typography: createResponsiveTypography(fonts.heading, fonts.body),
  shape,
  palette: {
    mode: "dark",
    primary: {
      main: primaryLight,
      light: primaryLight,
      dark: primaryColor,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: primaryColor,
      light: primaryLight,
      dark: primaryDark,
      contrastText: '#FFFFFF',
    },
    background: {
      default: darkBg,               // Gris oscuro suave, no negro puro
      paper: darkPaper,               // Paper oscuro
      subtle: "#1A1A1A",             // Fondo sutil
    },
    text: {
      primary: "#FAFAFA",            // Blanco suave
      secondary: "#A3A3A3",          // Gris claro
      disabled: "#737373",           // Gris medio
    },
    divider: darkBorder,              // Borde oscuro sutil
    action: {
      hover: alpha(primaryLight, 0.08),
      selected: alpha(primaryLight, 0.12),
    },
    error: {
      main: "#EF4444",
      contrastText: '#FFFFFF',
    },
    warning: {
      main: "#F59E0B",
      contrastText: '#FFFFFF',
    },
    info: {
      main: "#3B82F6",
      contrastText: '#FFFFFF',
    },
    success: {
      main: "#10B981",
      contrastText: '#FFFFFF',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: `${alpha(primaryLight, 0.2)} transparent`,
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
            backgroundColor: "transparent",
            width: 6,
          },
          "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            backgroundColor: alpha(primaryLight, 0.2),
            "&:hover": {
                backgroundColor: alpha(primaryLight, 0.3),
            }
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: darkPaper,
          borderBottom: `1px solid ${darkBorder}`,
          color: '#FAFAFA',
          boxShadow: 'none',
        }
      }
    },
    MuiDrawer: {
        styleOverrides: {
            paper: {
                backgroundColor: darkBg,
                borderRight: `1px solid ${darkBorder}`,
                color: '#FAFAFA',
            }
        }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: darkPaper,
          border: `1px solid ${darkBorder}`,
          color: '#FAFAFA',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: darkPaper,
          border: `1px solid ${darkBorder}`,
          borderRadius: 8,
          color: '#FAFAFA',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 24px',
          transition: 'all 0.2s ease',
        },
        containedPrimary: {
          backgroundColor: primaryLight,
          color: '#FFFFFF',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: primaryColor,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          }
        },
        outlinedPrimary: {
            borderColor: darkBorder,
            color: primaryLight,
            '&:hover': {
                borderColor: primaryLight,
                backgroundColor: alpha(primaryLight, 0.1),
            }
        }
      }
    },
    MuiTableCell: {
        styleOverrides: {
            root: {
                borderBottom: `1px solid ${darkBorder}`,
                color: '#FAFAFA',
            },
            head: {
                backgroundColor: alpha(primaryLight, 0.05),
                color: primaryLight,
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                letterSpacing: '0.05em',
            }
        }
    },
    MuiDialog: {
      defaultProps: {
        maxWidth: 'md',
        fullWidth: true,
      },
      styleOverrides: {
        paper: {
          borderRadius: 12,
          backgroundColor: darkPaper,
          border: `1px solid ${darkBorder}`,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
          '@media (min-width: 900px)': {
            maxWidth: '85vw !important',
          },
          '@media (max-width: 600px)': {
            margin: 0,
            width: '100%',
            height: '100%',
            maxHeight: '100%',
            borderRadius: 0,
            border: 'none',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          '@media (max-width: 600px)': {
            padding: '12px 16px',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          '@media (max-width: 600px)': {
            padding: '16px',
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          '@media (max-width: 600px)': {
            padding: '12px 16px',
            flexDirection: 'column',
            '& > *': {
              width: '100% !important',
              marginLeft: '0 !important',
              marginRight: '0 !important',
              marginBottom: '8px',
            },
            '& > *:last-child': {
              marginBottom: 0,
            },
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          '&:before': {
            display: 'none',
          },
          '@media (max-width: 600px)': {
            margin: '0 !important',
            borderRadius: '0 !important',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            padding: '4px 12px',
            minHeight: '48px !important',
            '&.Mui-expanded': {
              minHeight: '48px !important',
            },
          },
        },
        content: {
          '@media (max-width: 600px)': {
            margin: '8px 0 !important',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            padding: '8px 12px 12px',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: alpha(primaryLight, 0.15),
          color: primaryLight,
          fontWeight: 600,
        }
      }
    }
  }
});

export { lightTheme, darkTheme };
